import { useTranslation } from 'react-i18next'
import { useContractProperties } from '../../hooks/useQueries'
import { useFormatters } from '../../lib/format'
import { Badge } from '../ui/badge'
import { useAssemblyAttendance, useAssemblyChildren } from './assembly-api'
import { summarizeResolutions } from './assembly-utils'

function Fact({ label, children, testId }: { label: string; children: React.ReactNode; testId: string }) {
    return (
        <div className="min-w-0" data-testid={testId}>
            <dt className="text-xs text-muted-foreground">{label}</dt>
            <dd className="truncate text-sm font-medium">{children}</dd>
        </div>
    )
}

/**
 * The facts of an assembly in one row: date, place, attendance, how many
 * resolutions and how they went, status. The minutes' clause list lives in the
 * Details tab. Spec 2026-10-10-08 §7.
 */
export function AssemblySummary({ org, uid, status }: { org: string; uid: string; status: string }) {
    const { t } = useTranslation('assemblies')
    const { formatDate } = useFormatters()
    const { data: props } = useContractProperties(org, uid)
    const { data: children } = useAssemblyChildren(org, uid)
    const { data: sheet } = useAssemblyAttendance(org, uid)

    const clause = (code: string): unknown => props?.find((p) => p.property_definition?.code === code)?.value
    const heldAt = typeof clause('held_at') === 'string' ? (clause('held_at') as string) : ''
    const place = typeof clause('held_location') === 'string' ? (clause('held_location') as string) : ''
    const counts = summarizeResolutions(children?.data ?? [])
    const parties = sheet?.parties ?? []
    const total = sheet?.total_voices ?? 0
    const attending = parties
        .filter((p) => p.presence === 'present' || p.presence === 'represented' || p.presence === 'mail_vote')
        .reduce((sum, p) => sum + (p.voices ?? 0), 0)
    const dash = '—'

    return (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border bg-card p-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="assembly-summary">
            <Fact label={t('summary.date')} testId="assembly-summary-date">
                {heldAt ? formatDate(heldAt, { dateStyle: 'long' }) : dash}
            </Fact>
            <Fact label={t('summary.place')} testId="assembly-summary-place">{place || dash}</Fact>
            <Fact label={t('summary.attendance')} testId="assembly-summary-attendance">
                {total > 0 ? t('summary.attendance_value', { present: attending, total }) : dash}
            </Fact>
            <Fact label={t('summary.resolutions')} testId="assembly-summary-resolutions">
                {t('summary.resolutions_value', { count: counts.total })}
            </Fact>
            <Fact label={t('summary.results')} testId="assembly-summary-results">
                <span className="inline-flex flex-wrap gap-1">
                    {(sheet as { held?: boolean } | undefined)?.held ? (
                        <>
                            <Badge variant="success" data-testid="assembly-summary-adopted">{t('summary.adopted', { count: counts.adopted })}</Badge>
                            <Badge variant="destructive" data-testid="assembly-summary-rejected">{t('summary.rejected', { count: counts.rejected })}</Badge>
                        </>
                    ) : (
                        <Badge variant="outline" data-testid="assembly-summary-on-agenda">{t('summary.on_agenda', { count: counts.total })}</Badge>
                    )}
                </span>
            </Fact>
            <Fact label={t('summary.status')} testId="assembly-summary-status">{t(`status.${status}`)}</Fact>
        </dl>
    )
}
